import experiment from 'experiment';
import { BaseEntryScope, createEntryPoint } from '#packages/apilib';
import {
  EditorAPIKey,
  EditorCoreApiKey,
  SiteGlobalDataApiKey,
} from '#packages/apis';
import { sections as sectionsUtils } from '#packages/util';
import { events } from '#packages/coreBi';
import * as stateManagement from '#packages/stateManagement';
import { type EditorAPI } from '#packages/editorAPI';

import { AiApiKey } from './publicApiKey';
import { AiPublicApi } from './api';
import { INJECTION_BLACK_LIST } from './injectionBlackList';
import type { PanelDescriptor } from '#packages/stateManagement';

export class AiScope extends BaseEntryScope {
  editorAPI = this.useDependency(EditorAPIKey);
  editorCoreAPI = this.useDependency(EditorCoreApiKey);
  siteGlobalDataAPI = this.useDependency(SiteGlobalDataApiKey);
}

const AI_WELCOME_PANEL = 'ai.panels.welcomePanel';

const isSiteBlank = (editorAPI: EditorAPI): boolean => {
  const pages = editorAPI.pages.getPagesList();
  if (pages.length > 1) return false;
  const pageChildren = editorAPI.components.getChildren(pages[0]);
  if (pageChildren.length === 0) return true;
  if (pageChildren.length > 1) return false;
  const sectionChildren = editorAPI.components.getChildren(pageChildren[0]);
  return sectionChildren.length === 0;
};

export const AiEntryPoint = createEntryPoint({
  name: 'AiApi',
  Scope: AiScope,
  publicApi({ contributeApi }) {
    contributeApi(AiApiKey, AiPublicApi);
  },
  async initialize({ editorCoreAPI, editorAPI, siteGlobalDataAPI }: AiScope) {
    await editorCoreAPI.hooks._dsIsReady.promise;

    // check new site before silent installation
    const isNewSite =
      editorAPI.generalInfo.isFirstSave() || editorAPI.generalInfo.isDraft();

    await editorCoreAPI.hooks.initReady.promise;
    await editorCoreAPI.hooks.stageIsReadyAndVisible.promise;
    await siteGlobalDataAPI.getIsSiteGlobalDataReadyPromise();
    await editorCoreAPI.hooks.appsSilentInstallationReady.promise;

    const isInternalDesignerScanFlow = experiment.isOpen(
      'se_addDesignerSection',
    );

    const _isSiteBlank = isSiteBlank(editorAPI);
    const isBlackListed = INJECTION_BLACK_LIST.has(
      editorAPI.generalInfo.getSiteOriginalTemplateId(),
    );
    const shouldEnableAIFeaturesInSchoolMode =
      stateManagement.schoolMode.selectors.shouldEnableAIFeatures(
        editorAPI.store.getState(),
      );
    if (
      experiment.isOpen('se_aiInjectionIntoTemplateAllPages') &&
      isNewSite &&
      !_isSiteBlank &&
      shouldEnableAIFeaturesInSchoolMode &&
      !isBlackListed &&
      !isInternalDesignerScanFlow &&
      sectionsUtils.isSectionsEnabled()
    ) {
      editorAPI.bi.event(events.aiTextGenerator.panelOpenIntent, {
        panelType: 'injection to site',
        is_intend: true,
      });

      editorAPI.panelManager.addPanelFilter(
        'BLOCK_OPEN_PANELS_IF_AI_WELCOME_OPENED',
        (panelDescriptor: PanelDescriptor) => {
          return (
            editorAPI.panelManager.isPanelOpened(AI_WELCOME_PANEL) &&
            (panelDescriptor.frameType === 'leftPanelFrame' ||
              panelDescriptor.name === 'quickEdit')
          );
        },
      );
      editorAPI.panelManager.openPanel(AI_WELCOME_PANEL);
    } else {
      editorAPI.bi.event(events.aiTextGenerator.panelOpenIntent, {
        panelType: 'injection to site',
        is_intend: false,
      });
    }
  },
});
